// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Group, Loader, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { Coding } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { PATIENT_SYSTEM_STATUS, POLARIS_SYSTEM_STATUS_TAG_SYSTEM } from '@polarissleep/shared';
import { DataTable } from 'mantine-datatable';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { TableAnchor } from '../../components/layout/TableAnchor';
import { type InactiveTriageEntry, useInactiveTriageQueue } from '../../hooks/useInactiveTriageQueue';

export function InactiveTable({
  page,
  pageSize,
  search,
  onPageChange,
  onTotalChange,
  hideTestPatients,
}: {
  page: number;
  pageSize: number;
  search: string;
  onPageChange: (page: number) => void;
  onTotalChange?: (total: number) => void;
  hideTestPatients: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const { entries, total, loading } = useInactiveTriageQueue({ page, pageSize, search, hideTestPatients });
  useEffect(() => onTotalChange?.(total), [onTotalChange, total]);
  const reactivate = async (entry: InactiveTriageEntry): Promise<void> => {
    try {
      const patient = await medplum.readResource('Patient', entry.id);
      const tags: Coding[] = (patient.meta?.tag ?? []).filter(
        (tag: Coding) => tag.system !== POLARIS_SYSTEM_STATUS_TAG_SYSTEM
      );
      tags.push({ system: POLARIS_SYSTEM_STATUS_TAG_SYSTEM, code: PATIENT_SYSTEM_STATUS.active });
      await medplum.updateResource({ ...patient, meta: { ...patient.meta, tag: tags } });
      notifications.show({ color: 'green', message: 'Patient reactivated' });
    } catch (error) {
      notifications.show({
        color: 'red',
        message: error instanceof Error ? error.message : 'Unable to reactivate patient',
      });
    }
  };
  const columns = [
    {
      accessor: 'patientName' as const,
      title: 'Patient',
      render: (entry: InactiveTriageEntry) => (
        <Stack gap={0}>
          <TableAnchor size="sm" onClick={() => navigate(`/Patient/${entry.id}`)} style={{ cursor: 'pointer' }}>
            {entry.patientName}
          </TableAnchor>
          <Text size="xs" c="dimmed">
            {entry.patientSubtitle}
          </Text>
        </Stack>
      ),
    },
    {
      accessor: 'status' as const,
      title: 'Status',
      render: () => (
        <Badge color="gray" variant="light" radius="xl">
          Inactive
        </Badge>
      ),
    },
    {
      accessor: 'reason' as const,
      title: 'Reason',
      render: (entry: InactiveTriageEntry) => <Text size="sm">{entry.reason}</Text>,
    },
    {
      accessor: 'lastStage' as const,
      title: 'Last stage',
      render: (entry: InactiveTriageEntry) => <Text size="sm">{entry.lastStage}</Text>,
    },
    {
      accessor: 'lastActive' as const,
      title: 'Last activity',
      render: (entry: InactiveTriageEntry) => (
        <Text size="sm">{entry.lastActive ? new Date(entry.lastActive).toLocaleDateString() : '—'}</Text>
      ),
    },
    {
      accessor: 'actions' as const,
      title: 'Actions',
      render: (entry: InactiveTriageEntry) => (
        <Group gap="sm">
          <TableAnchor size="sm" onClick={() => navigate(`/Patient/${entry.id}`)} style={{ cursor: 'pointer' }}>
            View chart
          </TableAnchor>
          <TableAnchor size="sm" onClick={() => reactivate(entry).catch(console.error)} style={{ cursor: 'pointer' }}>
            Reactivate
          </TableAnchor>
        </Group>
      ),
    },
  ];
  if (loading && entries.length === 0) {
    return <Loader size="sm" />;
  }
  return (
    <DataTable
      striped
      highlightOnHover
      records={entries}
      columns={columns}
      idAccessor="id"
      noRecordsText="No inactive patients"
      page={page}
      recordsPerPage={pageSize}
      totalRecords={total}
      onPageChange={onPageChange}
    />
  );
}
